/**
 * Admin CRUD schemas for `system_settings` (Story 3.16).
 *
 * Source of truth: `docs/architecture/database-schema.md` §4.14 +
 * `apps/api/scripts/seed.ts` `SETTINGS` array (the 19 known keys).
 *
 * **Validation by key (Story 3.16 §AC #2):** every editable key has a
 * discriminated schema so admins cannot, e.g., store `prompt_max_chars: "abc"`
 * or a negative `openai_temperature`. The schema is the backend's last line
 * of defense — the UI uses a friendlier per-type editor, but the API revalidates
 * via `validateSettingValue()` below before persisting.
 *
 * Note on `@sinclair/typebox`: the rest of the package uses TypeBox over Zod
 * to share runtime validators with Fastify's `TypeBoxValidatorCompiler` route
 * schemas. We follow that pattern here for consistency; "Zod" in the story body
 * is a generic placeholder term for "runtime validator".
 *
 * **Discriminated validation strategy:** instead of one giant `Type.Union`
 * (which would force every PATCH to be validated against all 19 sub-schemas
 * and surface unreadable errors), we expose a per-key registry that the route
 * handler dispatches to after the body-level `value`/`rationale` envelope is
 * parsed. Errors carry the offending key + reason so the UI can highlight
 * exactly which field is wrong.
 */
import { Type } from '@sinclair/typebox';
import { AdminError } from './users.js';
// ---------------------------------------------------------------------------
// Known keys — single source of truth shared FE/BE.
// ---------------------------------------------------------------------------
/**
 * All editable system_settings keys (Story 3.16 §AC #6 — 19 keys total: 18
 * documented in the story body + `calendar_provider` from Story 7.4).
 *
 * Keys NOT in this list are filtered out of the admin UI (they exist in the
 * DB but are managed by other workflows). The route's `validateSettingValue`
 * rejects PATCHes against unknown keys with `UNKNOWN_KEY`.
 */
export const SYSTEM_SETTING_KEYS = [
    // Interruptor mestre da IA (inbound + outbound). Boolean.
    'agent_enabled',
    'business_hours',
    'opt_in_text',
    'handoff_sla_minutes',
    'handoff_operators',
    'lgpd_retention_days',
    'opt_out_ai_confidence_min',
    'reschedule_max',
    'reactivation_max',
    'reactivation_thresholds',
    'attendance_callback_delay_min',
    // Story 4.6 — minutes between the initial attendance callback and the
    // single 24h reminder. Default 1440 (24h).
    'attendance_callback_reminder_min',
    'openai_default_model',
    'openai_fallback_model',
    'openai_temperature',
    'openai_max_tokens',
    'openai_timeout_ms',
    'anti_repetition_threshold',
    'prompt_max_chars',
    'prompt_banned_patterns',
    'calendar_provider',
    // Story 5.4 — SellerDistributor (round-robin) tuning surfaced in the
    // Settings UI (categoria "Operação"). `seller_distribution_rule` is the
    // strategy selector (MVP: `round_robin` only); `last_assigned_seller_id`
    // is the cursor that the rule UPDATEs atomically. Operator may reset the
    // cursor to `null` via the UI to restart the cycle.
    'seller_distribution_rule',
    'last_assigned_seller_id',
    // Story 5.5.2 — test phone allowlist for MVP internal validation. When
    // non-empty, the GHL cron worker processes ONLY leads whose phone matches
    // (E.164, exact match after normalization). Webhook is NOT affected.
    'ghl_test_phone_allowlist',
    // Story 7.4 follow-up — GHL location ID consumed por `GhlCalendarAdapter`
    // (lido em `calendar-factory.ts:loadGhlLocationId`). Surface editável aqui
    // para que admins configurem o location sem precisar de SQL direto.
    'ghl_location_id',
    // Story 3.24 follow-up (2026-07-16) — pina qual campanha `lead_new` os
    // leads vindos do GHL (webhook + cron) devem usar, agora que 2+ campanhas
    // lead_new podem estar ativas ao mesmo tempo (GHL não tem um conceito
    // nativo de "campanha" como a Guru tem via URL por funil). `null`/ausente
    // preserva o fallback legado (campanha lead_new ativa mais antiga).
    'ghl_lead_new_campaign_id',
    // GHL cron runtime controls — expostos no admin para que o operador possa
    // pausar o polling e ajustar a cadência sem redeploy (Story 2.8 §intent).
    'ghl_polling_enabled',
    'ghl_poll_interval_minutes',
    'ghl_appointment_sync_interval_minutes',
    // Allowlist de GHL User IDs cujos leads o cron processa. Vazio = sem
    // restrição (todos os leads elegíveis são processados). Não-vazio = somente
    // leads cujo `assignedTo` esteja na lista. Cron-only — webhook não consulta.
    'ghl_user_allowlist',
    // Mapeamento GHL userId → GHL calendarId. Usado pelo sistema ao criar
    // appointments para direcionar para o calendário correto do vendedor.
    'ghl_user_calendar_map',
    // Timings do fluxo de confirmação de agendamento.
    // `confirmation_c2_send_days_before`: quantos dias antes da reunião enviar C2
    //   (lembrete do dia). 0 = no próprio dia da reunião (padrão). 1 = véspera.
    // `confirmation_c3_hours_before`: quantas horas antes da reunião enviar C3
    //   (lembrete final). Padrão: 1 hora.
    // `confirmation_no_response_min_before`: minutos antes da reunião para checar
    //   se o cliente não respondeu. Padrão: 15 min.
    'confirmation_c2_send_days_before',
    'confirmation_c3_hours_before',
    'confirmation_no_response_min_before',
    // GHL Appointment Sync worker controls.
    // `ghl_appointment_sync_enabled`: boolean que habilita/desabilita o cron de
    //   sync de agendamentos. Padrão: true (cron sempre roda).
    // `ghl_last_appointment_sync_at`: ISO string do último sync bem-sucedido.
    //   Escrito pelo cron; leitura-only no admin (diagnóstico).
    'ghl_appointment_sync_enabled',
    'ghl_last_appointment_sync_at',
    // 2026-07-27 (user request) — multi-provider AI. `ai_provider_active`
    // selects which of the 3 supported providers `chatCompletion` calls hit;
    // the other two keys pin the model for the non-OpenAI providers (OpenAI
    // already had `openai_default_model`/`openai_fallback_model` above).
    'ai_provider_active',
    'ai_provider_claude_model',
    'ai_provider_gemini_model',
    // Story 10.5 (2026-07-28, user request) — GHL sale sync (indicador de
    // vendas). Pipeline id is reused from `ghl_lead_new_pipeline` — only the
    // stage id + product custom-field id are new (Article IV: zero hardcoded
    // business rule, both configurable instead of literals in the worker).
    'ghl_sale_stage_id',
    'ghl_sale_product_field_id',
    'ghl_sale_sync_enabled',
    'ghl_sale_sync_interval_minutes',
    'ghl_last_sale_sync_at',
    // Story 10.8 (2026-07-30, user request, AC7) — taxa de câmbio pro painel
    // de ROI do dashboard.
    'usd_to_brl_rate',
    // Story 10.8 (2026-07-30, user request, AC4) — motivos de perda,
    // sincronizados de Opportunities `status='lost'` no GHL (mesmo espírito de
    // ghl_sale_stage_id/ghl_sale_product_field_id, Story 10.5, mas pro lado
    // "perdido" em vez de "vendido").
    'ghl_lost_stage_id',
    'ghl_lost_reason_field_id',
    // Story 10.8 (2026-07-30, user request, AC5) — cache do "Insight Principal"
    // gerado por IA, escrito por `dashboard-insight-scheduler.worker.ts`
    // (cron-tick 180min). Não editável via UI.
    'dashboard_insight_cache',
];
export function isKnownSettingKey(value) {
    return SYSTEM_SETTING_KEYS.includes(value);
}
/**
 * Fixed enum options surfaced as <Select> in the UI. Each list is the
 * authoritative set of accepted values for that key.
 */
export const OPENAI_MODEL_OPTIONS = [
    'gpt-4o-mini',
    'gpt-4o',
    'gpt-4-turbo',
    'gpt-4',
    'gpt-3.5-turbo',
];
export const CALENDAR_PROVIDER_OPTIONS = ['google', 'ghl'];
/** 2026-07-27 (user request) — multi-provider AI selector + per-provider model lists. */
export const AI_PROVIDER_OPTIONS = ['openai', 'gemini', 'claude'];
export const CLAUDE_MODEL_OPTIONS = ['claude-sonnet-5', 'claude-opus-4-8', 'claude-haiku-4-5'];
export const GEMINI_MODEL_OPTIONS = ['gemini-2.0-flash', 'gemini-1.5-pro'];
/**
 * Story 5.4 — distribution rule options. MVP exposes only `round_robin`;
 * `load_based` / `specialty_based` are out of scope (Story 5.4 §Out of
 * scope) but the list is the single source of truth for the
 * `getDistributionRule` factory + the Settings UI <Select>.
 */
export const SELLER_DISTRIBUTION_RULE_OPTIONS = ['round_robin'];
// ---------------------------------------------------------------------------
// Helper validators (pure functions, no TypeBox).
// Used by the route handler to validate the `value` jsonb against the matching
// key-level schema. Returning `null` means valid; a string means the reason.
// ---------------------------------------------------------------------------
function isPlainObject(v) {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function isNumberInRange(v, min, max) {
    return typeof v === 'number' && Number.isFinite(v) && v >= min && v <= max;
}
function isPositiveInteger(v) {
    return typeof v === 'number' && Number.isInteger(v) && v > 0;
}
function isNonNegativeInteger(v) {
    return typeof v === 'number' && Number.isInteger(v) && v >= 0;
}
function isNonEmptyString(v, maxLen = 5000) {
    return typeof v === 'string' && v.length > 0 && v.length <= maxLen;
}
function isStringArray(v, maxLen = 200) {
    return (Array.isArray(v) &&
        v.every((item) => typeof item === 'string' && item.length > 0 && item.length <= maxLen));
}
function isUuidArray(v) {
    if (!Array.isArray(v))
        return false;
    const re = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;
    return v.every((item) => typeof item === 'string' && re.test(item));
}
function isUuidOrNull(v) {
    if (v === null)
        return true;
    if (typeof v !== 'string')
        return false;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu.test(v);
}
/** GHL IDs são strings alfanuméricas opacas (ex: "5iI1L7UTfrP6gc7lB728"). */
function isGhlIdStringArray(v) {
    return isStringArray(v, 64);
}
/**
 * `ghl_user_calendar_map` — objeto plano `{ [ghlUserId]: ghlCalendarId }`.
 * Ambas chaves e valores são strings não-vazias ≤ 64 chars.
 */
function isGhlStringMap(v) {
    if (!isPlainObject(v))
        return false;
    for (const [k, val] of Object.entries(v)) {
        if (!isNonEmptyString(k, 64))
            return false;
        if (!isNonEmptyString(val, 64))
            return false;
    }
    return true;
}
/**
 * `dashboard_insight_cache` (Story 10.8, 2026-07-30, user request, AC5) —
 * `null` (nunca sincronizado ainda) OU `{ text, generatedAt, periodFrom,
 * periodTo }`, todos ISO date strings exceto `text`. Escrito exclusivamente
 * por `dashboard-insight-scheduler.worker.ts`; não é editável via UI (não
 * consta em `settings-categories.ts`) — a validação aqui é só defesa em
 * profundidade, mesmo espírito de `ghl_last_sale_sync_at`.
 */
function isDashboardInsightCache(v) {
    if (v === null)
        return true;
    if (!isPlainObject(v))
        return false;
    return (typeof v.text === 'string' &&
        typeof v.generatedAt === 'string' &&
        !Number.isNaN(Date.parse(v.generatedAt)) &&
        typeof v.periodFrom === 'string' &&
        !Number.isNaN(Date.parse(v.periodFrom)) &&
        typeof v.periodTo === 'string' &&
        !Number.isNaN(Date.parse(v.periodTo)));
}
function isHhMm(v) {
    return typeof v === 'string' && /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(v);
}
const WEEKDAYS = new Set(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']);
function isBusinessHours(v) {
    if (!isPlainObject(v))
        return false;
    const tz = v.timezone;
    const start = v.start;
    const end = v.end;
    const days = v.days;
    if (typeof tz !== 'string' || tz.length === 0)
        return false;
    if (!isHhMm(start))
        return false;
    if (!isHhMm(end))
        return false;
    if (!Array.isArray(days))
        return false;
    if (days.length === 0)
        return false;
    return days.every((d) => typeof d === 'string' && WEEKDAYS.has(d));
}
function isValidRegexArray(v) {
    if (!isStringArray(v, 500))
        return false;
    for (const pattern of v) {
        try {
            void new RegExp(pattern);
        }
        catch {
            return false;
        }
    }
    return true;
}
/**
 * Story 5.5.2 — E.164 array validator. Each element must be `+` followed by
 * 1..15 digits with the first digit in 1..9 (E.164 spec). Returns the list of
 * offending entries (empty if all valid) so the route handler can surface
 * exactly which strings failed.
 *
 * NOTE: this validates FORMAT only — call-sites (the cron worker) must still
 * canonicalize each entry via the API's `normalizePhoneE164` helper before
 * comparing against incoming phones. That helper strips spaces / dashes / etc.;
 * here we keep it strict so admins do not store "+55 11 99999-9999" and
 * mistakenly mismatch a normalized lead phone.
 */
const E164_RE = /^\+[1-9]\d{1,14}$/;
function e164ArrayOffenders(v) {
    if (!Array.isArray(v))
        return null;
    const bad = [];
    for (const item of v) {
        if (typeof item !== 'string' || !E164_RE.test(item)) {
            bad.push(typeof item === 'string' ? item : String(item));
        }
    }
    return bad;
}
/**
 * Per-key validator. Returns `null` when `value` is acceptable for `key`,
 * or a human-readable error message when invalid.
 *
 * The route handler dispatches to this AFTER the envelope (PATCH body) has
 * been parsed. Unknown keys are rejected upstream.
 */
export function validateSettingValue(key, value) {
    switch (key) {
        case 'business_hours':
            return isBusinessHours(value)
                ? null
                : '`business_hours` deve ser { timezone, start (HH:mm), end (HH:mm), days: string[] }';
        case 'opt_in_text':
            return isNonEmptyString(value, 2000)
                ? null
                : '`opt_in_text` deve ser string não-vazia (≤2000 chars)';
        case 'handoff_sla_minutes':
            return isPositiveInteger(value) && value <= 240
                ? null
                : '`handoff_sla_minutes` deve ser inteiro 1..240';
        case 'handoff_operators':
            return isUuidArray(value) ? null : '`handoff_operators` deve ser array de UUIDs';
        case 'lgpd_retention_days':
            return isPositiveInteger(value) && value <= 3650
                ? null
                : '`lgpd_retention_days` deve ser inteiro 1..3650';
        case 'opt_out_ai_confidence_min':
            return isNumberInRange(value, 0, 1)
                ? null
                : '`opt_out_ai_confidence_min` deve estar entre 0 e 1';
        case 'reschedule_max':
            return isNonNegativeInteger(value) && value <= 10
                ? null
                : '`reschedule_max` deve ser inteiro 0..10';
        case 'reactivation_max':
            return isNonNegativeInteger(value) && value <= 10
                ? null
                : '`reactivation_max` deve ser inteiro 0..10';
        case 'reactivation_thresholds':
            return isStringArray(value, 16) && value.length > 0
                ? null
                : '`reactivation_thresholds` deve ser array de strings não-vazio (ex: ["1d","3d","7d"])';
        case 'attendance_callback_delay_min':
            return isNonNegativeInteger(value) && value <= 1440
                ? null
                : '`attendance_callback_delay_min` deve ser inteiro 0..1440';
        case 'attendance_callback_reminder_min':
            // Reminder cadence — must be ≥ delay (no point reminding before the
            // initial callback fired). MVP allows the full 0..2880 (48h) range and
            // documents the operational guidance in the runbook.
            return isPositiveInteger(value) && value <= 2880
                ? null
                : '`attendance_callback_reminder_min` deve ser inteiro 1..2880 (min entre o callback inicial e o lembrete único)';
        case 'openai_default_model':
        case 'openai_fallback_model':
            return typeof value === 'string' &&
                OPENAI_MODEL_OPTIONS.includes(value)
                ? null
                : `\`${key}\` deve ser um dos modelos suportados: ${OPENAI_MODEL_OPTIONS.join(', ')}`;
        case 'openai_temperature':
            return isNumberInRange(value, 0, 2)
                ? null
                : '`openai_temperature` deve estar entre 0 e 2';
        case 'openai_max_tokens':
            return isPositiveInteger(value) && value <= 16000
                ? null
                : '`openai_max_tokens` deve ser inteiro 1..16000';
        case 'openai_timeout_ms':
            return isPositiveInteger(value) && value <= 60000
                ? null
                : '`openai_timeout_ms` deve ser inteiro 1..60000';
        case 'anti_repetition_threshold':
            return isNumberInRange(value, 0, 1)
                ? null
                : '`anti_repetition_threshold` deve estar entre 0 e 1';
        case 'prompt_max_chars':
            return isPositiveInteger(value) &&
                value >= 100 &&
                value <= 50000
                ? null
                : '`prompt_max_chars` deve ser inteiro 100..50000';
        case 'prompt_banned_patterns':
            return isValidRegexArray(value)
                ? null
                : '`prompt_banned_patterns` deve ser array de regex válidas (sintaxe JavaScript)';
        case 'calendar_provider':
            return typeof value === 'string' &&
                CALENDAR_PROVIDER_OPTIONS.includes(value)
                ? null
                : `\`calendar_provider\` deve ser um dos: ${CALENDAR_PROVIDER_OPTIONS.join(', ')}`;
        case 'seller_distribution_rule':
            return typeof value === 'string' &&
                SELLER_DISTRIBUTION_RULE_OPTIONS.includes(value)
                ? null
                : `\`seller_distribution_rule\` deve ser um dos: ${SELLER_DISTRIBUTION_RULE_OPTIONS.join(', ')}`;
        case 'last_assigned_seller_id':
            return isUuidOrNull(value)
                ? null
                : '`last_assigned_seller_id` deve ser UUID ou null (reset do cursor round-robin)';
        case 'ghl_test_phone_allowlist': {
            const offenders = e164ArrayOffenders(value);
            if (offenders === null) {
                return '`ghl_test_phone_allowlist` deve ser array de strings E.164';
            }
            if (offenders.length > 0) {
                return `\`ghl_test_phone_allowlist\` contém entradas fora do formato E.164: ${offenders.join(', ')}`;
            }
            return null;
        }
        case 'ghl_location_id':
            // GHL location IDs são strings opacas (não-UUID estrito). Aceitamos
            // qualquer string não-vazia ≤64 chars. Backend valida adicionalmente
            // ao tentar usar (GhlCalendarAdapter retorna erro descritivo se o
            // location ID for inválido na chamada à API GHL).
            return isNonEmptyString(value, 64)
                ? null
                : '`ghl_location_id` deve ser string não-vazia (≤64 chars)';
        case 'ghl_lead_new_campaign_id':
            return isUuidOrNull(value)
                ? null
                : '`ghl_lead_new_campaign_id` deve ser UUID de uma campanha lead_new ou null (usa o fallback legado)';
        case 'agent_enabled':
            return typeof value === 'boolean'
                ? null
                : '`agent_enabled` deve ser boolean (true = IA ligada, false = pausada)';
        case 'ghl_polling_enabled':
            return typeof value === 'boolean'
                ? null
                : '`ghl_polling_enabled` deve ser boolean (true = ativo, false = pausado)';
        case 'ghl_poll_interval_minutes':
            return typeof value === 'number' &&
                Number.isInteger(value) &&
                value >= 1 &&
                value <= 60
                ? null
                : '`ghl_poll_interval_minutes` deve ser inteiro 1..60';
        case 'ghl_appointment_sync_interval_minutes':
            return typeof value === 'number' &&
                Number.isInteger(value) &&
                value >= 1 &&
                value <= 60
                ? null
                : '`ghl_appointment_sync_interval_minutes` deve ser inteiro 1..60';
        case 'ghl_user_allowlist':
            return isGhlIdStringArray(value)
                ? null
                : '`ghl_user_allowlist` deve ser array de strings GHL User ID (≤64 chars cada). Vazio = sem restrição.';
        case 'ghl_user_calendar_map':
            return isGhlStringMap(value)
                ? null
                : '`ghl_user_calendar_map` deve ser objeto { ghlUserId: ghlCalendarId } com strings não-vazias (≤64 chars)';
        case 'confirmation_c2_send_days_before':
            return isNonNegativeInteger(value) && value <= 7
                ? null
                : '`confirmation_c2_send_days_before` deve ser inteiro 0..7 (0 = no dia da reunião, 1 = véspera)';
        case 'confirmation_c3_hours_before':
            return isPositiveInteger(value) && value <= 48
                ? null
                : '`confirmation_c3_hours_before` deve ser inteiro 1..48';
        case 'confirmation_no_response_min_before':
            return isPositiveInteger(value) && value <= 120
                ? null
                : '`confirmation_no_response_min_before` deve ser inteiro 1..120';
        case 'ghl_appointment_sync_enabled':
            return typeof value === 'boolean'
                ? null
                : '`ghl_appointment_sync_enabled` deve ser boolean (true/false)';
        case 'ghl_last_appointment_sync_at':
            // `null` é o valor legítimo antes do primeiro sync bem-sucedido — não
            // apenas uma string ISO (QA fix, Story 10.5 review: o seed grava
            // `null` como default; sem isso, resetar a chave via PATCH falhava).
            return value === null || (typeof value === 'string' && !Number.isNaN(Date.parse(value)))
                ? null
                : '`ghl_last_appointment_sync_at` deve ser ISO date string ou null';
        case 'ai_provider_active':
            return typeof value === 'string' && AI_PROVIDER_OPTIONS.includes(value)
                ? null
                : `\`ai_provider_active\` deve ser um dos: ${AI_PROVIDER_OPTIONS.join(', ')}`;
        case 'ai_provider_claude_model':
            return typeof value === 'string' && CLAUDE_MODEL_OPTIONS.includes(value)
                ? null
                : `\`ai_provider_claude_model\` deve ser um dos: ${CLAUDE_MODEL_OPTIONS.join(', ')}`;
        case 'ai_provider_gemini_model':
            return typeof value === 'string' && GEMINI_MODEL_OPTIONS.includes(value)
                ? null
                : `\`ai_provider_gemini_model\` deve ser um dos: ${GEMINI_MODEL_OPTIONS.join(', ')}`;
        case 'ghl_sale_stage_id':
        case 'ghl_sale_product_field_id':
            return isNonEmptyString(value, 64)
                ? null
                : `\`${key}\` deve ser string não-vazia (≤64 chars) — id opaco do GHL`;
        case 'ghl_sale_sync_enabled':
            return typeof value === 'boolean'
                ? null
                : '`ghl_sale_sync_enabled` deve ser boolean (true/false)';
        case 'ghl_sale_sync_interval_minutes':
            return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 60
                ? null
                : '`ghl_sale_sync_interval_minutes` deve ser inteiro 1..60';
        case 'ghl_last_sale_sync_at':
            // `null` é o valor legítimo antes do primeiro sync bem-sucedido (é o
            // que o seed grava como default) — QA fix, Story 10.5 review.
            return value === null || (typeof value === 'string' && !Number.isNaN(Date.parse(value)))
                ? null
                : '`ghl_last_sale_sync_at` deve ser ISO date string ou null';
        case 'usd_to_brl_rate':
            return typeof value === 'number' && value > 0
                ? null
                : '`usd_to_brl_rate` deve ser um número positivo';
        case 'ghl_lost_stage_id':
        case 'ghl_lost_reason_field_id':
            // Vazio = feature desligada (motivos de perda não sincroniza) — mesmo
            // espírito de `cadence.c4_offset` (vazio = toque desligado).
            return typeof value === 'string' && value.length <= 64
                ? null
                : `\`${key}\` deve ser string (≤64 chars) — vazio = motivos de perda desligado`;
        case 'dashboard_insight_cache':
            return isDashboardInsightCache(value)
                ? null
                : '`dashboard_insight_cache` deve ser null ou { text, generatedAt, periodFrom, periodTo }';
        default: {
            // Exhaustiveness guard — surfaces an unmapped key at compile time.
            const _exhaustive = key;
            void _exhaustive;
            return 'chave desconhecida';
        }
    }
}
// ---------------------------------------------------------------------------
// TypeBox schemas — route-level envelopes.
// ---------------------------------------------------------------------------
/**
 * `SystemSetting` — public projection returned by GET endpoints. `value` is
 * the raw jsonb (no narrowing) so the UI can render per-key editors generically.
 */
export const SystemSetting = Type.Object({
    key: Type.String({ minLength: 1 }),
    value: Type.Unknown(),
    description: Type.Union([Type.String(), Type.Null()]),
    updatedBy: Type.Union([Type.String({ format: 'uuid' }), Type.Null()]),
    updatedAt: Type.String({ format: 'date-time' }),
});
export const SystemSettingListResponse = Type.Object({
    settings: Type.Array(SystemSetting),
});
export const SystemSettingResponse = Type.Object({
    setting: SystemSetting,
});
/**
 * `SystemSettingUpdate` — body of `PATCH /admin/system-settings/:key`.
 *
 * `value` is whatever jsonb the key accepts (validated server-side via
 * `validateSettingValue`). `rationale` is required for audit traceability
 * (Story 3.16 §AC #3 + Decision 1).
 */
export const SystemSettingUpdate = Type.Object({
    value: Type.Unknown(),
    rationale: Type.String({ minLength: 10, maxLength: 500 }),
});
export const SystemSettingKeyParam = Type.Object({
    key: Type.String({ minLength: 1, maxLength: 100 }),
});
// Re-export AdminError so route imports stay consistent.
export { AdminError };
// Exhaustiveness sanity check — referenced solely so TS treats every key as
// "used". The `validateSettingValue` switch already enforces exhaustiveness
// at compile time.
const _typecheck = SystemSetting;
void _typecheck;
//# sourceMappingURL=system-settings.js.map